import { User } from "../types/user";
import { State } from "../types/state";
import { signalStore, withMethods, withState, patchState, withComputed } from '@ngrx/signals';
import { computed, inject } from "@angular/core";
import { StateAndAssociationService } from "../shared/api-services";
import { debounceTime, filter, map, pipe, switchMap } from "rxjs";
import { DEFAULT_SESSION_PERMISSIONS } from "../types/permissions";
import { UserService } from "../shared/user.service";
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';

export interface AppState {
  states: State[];
  user: User;
  userLoaded: boolean;
}

const blankUser: User = {
    guid: '',
    name: '',
    permissions: DEFAULT_SESSION_PERMISSIONS,
}

const initialState: AppState = {
  states: [],
  user: blankUser,
  userLoaded: false,
};

export const AppStore = signalStore(
  {
      providedIn: 'root'
  },
  withState(initialState),
  withComputed((store) => ({
    isStoreLoaded: computed(() => store.states().length > 0 && store.userLoaded()),
  })),
  //methods
  withMethods((
    store,
    stateService = inject(StateAndAssociationService),
    userService = inject(UserService)) => ({
    /**
     * Reset user data in the store
     */
    resetUserData(): void {
      patchState(store, {
        user: blankUser,
        userLoaded: false,
      });
    },
    /**
     * Load the store states
     */
    loadStore(): void {
      this.loadStates();
      this.loadUser();
    },
    /**
     * Load the user data
     */
    loadUser: rxMethod<void>(pipe(
      debounceTime(300),
      filter(() => !store.userLoaded()),
      switchMap(() =>
        userService.getUser().pipe(
          tapResponse({
            next: (user) => {
              if (user) {
                patchState(store, {
                  user,
                  userLoaded: true,
                });
              }
            },
            error: (err) => {
              console.error('Failed to load user data', err);
              patchState(store, {
                user: blankUser,
                userLoaded: false,
              });
            }
          }),
        )
      )
    )),
    /**
     * Load the states from the API and update the store
     */
    loadStates: rxMethod<void>(pipe(
      debounceTime(300),
      filter(() => store.states().length === 0),
      switchMap(() =>
        stateService.getStateAndAssociations().pipe(
          map((states) => {
            return states.filter(x => x.state && x.stateAbbr && x.state.trim() !== '' && x.stateAbbr.trim() !== '')
              .map(x => ({ name: x.state, abbreviation: x.stateAbbr }));
          }),
          tapResponse({
            next: (states) => {
              patchState(store, { states });
            },
            error: (err) => {
              console.error('Failed to load states', err);
              patchState(store, { states: [] });
            },
          }),
        )
      )
    )),
  })),
);
